/**
 * Development-only diagnostics.
 *
 * Two rules drive the shape of this module:
 *
 * 1. It has to vanish from production. Every call site is wrapped in `if (DEV)`, and `DEV`
 *    is `import.meta.env.DEV`, which Vite replaces with a literal `false` at build time —
 *    esbuild then drops the branch and the strings inside it. A production bundle contains
 *    no logging code and no log text at all.
 * 2. It must never be able to print user content. Recordings, project names, file names,
 *    captions and note text are the user's private material; a stray `log.debug(name)`
 *    would put them in a console that screen-recording and support tooling can capture.
 *    Field values are therefore typed as numbers, booleans, or a branded `LogTag` that only
 *    the helpers below can produce — none of which can be built out of user text by
 *    accident. Developer-authored literals go through `tag()`, which is a deliberate act.
 *
 * Because everything compiles out, production failures are surfaced to the user through the
 * app's banner/error UI rather than through a console. That is intentional: an error the
 * user cannot see is an error nobody fixes.
 */

const DEV: boolean = import.meta.env.DEV;

declare const logSafeBrand: unique symbol;

/** A string that has been vouched for as containing no user content. */
export type LogTag = string & { readonly [logSafeBrand]: 'log-safe' };

export type LogValue = number | boolean | null | undefined | LogTag;

export type LogFields = Readonly<Record<string, LogValue>>;

/**
 * Marks a developer-authored literal as safe to print. Never call this with a value that
 * came from a file, a project, a network response, or anything the user typed.
 */
export function tag(literal: string): LogTag {
  return literal as LogTag;
}

/** Milliseconds, rounded to a tenth — enough to spot a regression, no more. */
export function ms(value: number): LogTag {
  return `${Math.round(value * 10) / 10}ms` as LogTag;
}

export function bytes(value: number): LogTag {
  if (value < 1024) return `${Math.round(value)}B` as LogTag;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)}KB` as LogTag;
  return `${(value / (1024 * 1024)).toFixed(1)}MB` as LogTag;
}

/**
 * First eight characters of an identifier. Enough to correlate two log lines, not enough
 * to reconstruct anything about the sample or project behind it.
 */
export function idPrefix(identifier: string): LogTag {
  return identifier.slice(0, 8) as LogTag;
}

/** Records that a value existed and how long it was, without recording the value. */
export function shape(value: unknown): LogTag {
  if (value === null) return 'null' as LogTag;
  if (value === undefined) return 'undefined' as LogTag;
  if (Array.isArray(value)) return `array(${value.length})` as LogTag;
  if (typeof value === 'string') return `string(${value.length})` as LogTag;
  if (value instanceof Error) return `${value.name}` as LogTag;
  if (typeof value === 'object') return `object(${Object.keys(value).length})` as LogTag;
  return typeof value as LogTag;
}

type Level = 'debug' | 'info' | 'warn' | 'error';

const consoleMethod: Record<Level, 'debug' | 'info' | 'warn' | 'error'> = {
  debug: 'debug',
  info: 'info',
  warn: 'warn',
  error: 'error',
};

function emit(level: Level, scope: string, message: string, fields?: LogFields): void {
  const prefix = `%c${scope}%c ${message}`;
  const scopeStyle = 'color:#EC5CFF;font-weight:600';
  const reset = 'color:inherit;font-weight:inherit';
  if (fields === undefined) {
    console[consoleMethod[level]](prefix, scopeStyle, reset);
    return;
  }
  console[consoleMethod[level]](prefix, scopeStyle, reset, fields);
}

export interface Logger {
  /** True only in a development build. Guard expensive diagnostic work with it. */
  readonly enabled: boolean;
  debug(scope: string, message: string, fields?: LogFields): void;
  info(scope: string, message: string, fields?: LogFields): void;
  warn(scope: string, message: string, fields?: LogFields): void;
  /**
   * `cause` is printed as its class name and message only — an Error's own message is
   * written by us, not by the user, so it is safe; its payload is not printed.
   */
  error(scope: string, message: string, cause?: unknown, fields?: LogFields): void;
  /** Starts a duration measurement. Call the returned function to log the elapsed time. */
  time(scope: string, label: string, fields?: LogFields): () => void;
}

export const log: Logger = {
  enabled: DEV,

  debug(scope, message, fields) {
    if (DEV) emit('debug', scope, message, fields);
  },

  info(scope, message, fields) {
    if (DEV) emit('info', scope, message, fields);
  },

  warn(scope, message, fields) {
    if (DEV) emit('warn', scope, message, fields);
  },

  error(scope, message, cause, fields) {
    if (DEV) {
      const detail: Record<string, LogValue> = { ...fields };
      if (cause !== undefined) {
        detail['cause'] =
          cause instanceof Error ? (`${cause.name}: ${cause.message}` as LogTag) : shape(cause);
      }
      emit('error', scope, message, detail);
    }
  },

  time(scope, label, fields) {
    if (!DEV) return () => {};
    const started = performance.now();
    return () => {
      emit('debug', scope, label, { ...fields, elapsed: ms(performance.now() - started) });
    };
  },
};

export interface ScopedLogger {
  readonly enabled: boolean;
  debug(message: string, fields?: LogFields): void;
  info(message: string, fields?: LogFields): void;
  warn(message: string, fields?: LogFields): void;
  error(message: string, cause?: unknown, fields?: LogFields): void;
  time(label: string, fields?: LogFields): () => void;
}

/** `const log = createLogger('audio')` — the shape every module in PadLab uses. */
export function createLogger(scope: string): ScopedLogger {
  return {
    enabled: DEV,
    debug: (message, fields) => log.debug(scope, message, fields),
    info: (message, fields) => log.info(scope, message, fields),
    warn: (message, fields) => log.warn(scope, message, fields),
    error: (message, cause, fields) => log.error(scope, message, cause, fields),
    time: (label, fields) => log.time(scope, label, fields),
  };
}
